import { useMemo, useState } from "react";
import { ArrowRightLeft, GripVertical, MoveRight, TrendingUp, TrendingDown } from "lucide-react";
import { fmtRp, fmtRpShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Segmented, InfoNote } from "@/components/common/Cards";
import { Field, MoneyInput } from "@/components/common/Inputs";
import { REGIME_LABEL, TX_LABEL, DEFAULT_PROFILE } from "./eqUtils";

const GREEN = {
  box: "border-[hsl(160_45%_28%)] bg-[hsl(160_40%_8%)] hover:border-[hsl(160_55%_45%)]",
  text: "text-[hsl(160_55%_62%)]",
  head: "text-[hsl(160_45%_70%)]",
};
const RED = {
  box: "border-[hsl(0_45%_32%)] bg-[hsl(0_40%_9%)] hover:border-[hsl(0_60%_50%)]",
  text: "text-[hsl(0_72%_68%)]",
  head: "text-[hsl(0_50%_75%)]",
};

function boxHeight(amount, max) {
  if (!max || amount <= 0) return 48;
  return Math.round(48 + Math.sqrt(amount / max) * 72);
}

export function EqualizerBoard({ companies, preview, onRequestMove, dragState, setDragState }) {
  const max = useMemo(() => {
    let m = 0;
    Object.values(preview).forEach((items) => items.forEach((i) => (m = Math.max(m, i.amount))));
    return m;
  }, [preview]);
  const [hoverCol, setHoverCol] = useState(null);

  return (
    <div className="overflow-x-auto pb-2" data-testid="eq-board">
      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${companies.length}, minmax(260px, 1fr))` }}>
        {companies.map((c) => {
          const items = preview[c.id] || [];
          const rev = items.filter((i) => i.section === "pendapatan");
          const exp = items.filter((i) => i.section === "beban");
          const totRev = rev.reduce((s, i) => s + i.amount, 0);
          const totExp = exp.reduce((s, i) => s + i.amount, 0);
          const p = { ...DEFAULT_PROFILE, ...(c.profile || {}) };
          const canDrop = dragState && dragState.fromId !== c.id;
          return (
            <div
              key={c.id}
              className={`rounded-xl border bg-card p-3 elev-1 transition-colors ${hoverCol === c.id && canDrop ? "border-primary ring-1 ring-primary/50" : canDrop ? "border-dashed border-primary/50" : "border-border"}`}
              onDragOver={(e) => {
                if (canDrop) {
                  e.preventDefault();
                  setHoverCol(c.id);
                }
              }}
              onDragLeave={() => setHoverCol((h) => (h === c.id ? null : h))}
              onDrop={(e) => {
                e.preventDefault();
                setHoverCol(null);
                if (canDrop) onRequestMove({ fromId: dragState.fromId, itemId: dragState.itemId, toId: c.id });
                setDragState(null);
              }}
              data-testid={`eq-board-col-${c.id}`}
            >
              <div className="px-1 pb-3 border-b border-border">
                <div className="font-display text-lg leading-tight truncate" title={c.name}>{c.name}</div>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">{REGIME_LABEL[p.regime]}</Badge>
                  <Badge variant="outline" className="text-[10px] border-border">{p.is_pkp ? "PKP" : "Non-PKP"}</Badge>
                </div>
                <div className="text-[11px] text-muted-foreground mt-2 num">
                  Laba komersial <span className={totRev - totExp < 0 ? RED.text : "text-primary"}>{fmtRpShort(totRev - totExp)}</span>
                </div>
              </div>
              <Section title="Pendapatan" total={totRev} tone={GREEN} items={rev} max={max} company={c} onRequestMove={onRequestMove} setDragState={setDragState} />
              <Section title="Beban" total={totExp} tone={RED} items={exp} max={max} company={c} onRequestMove={onRequestMove} setDragState={setDragState} />
              {canDrop && (
                <div className="mt-3 rounded-lg border border-dashed border-primary/60 py-3 text-center text-xs text-primary" aria-hidden>
                  Lepas di sini untuk memindahkan ke {c.name}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Section({ title, total, tone, items, max, company, onRequestMove, setDragState }) {
  return (
    <div className="mt-3">
      <div className={`flex items-center justify-between px-1 text-[11px] uppercase tracking-[0.12em] ${tone.head}`}>
        <span>{title}</span>
        <span className="num normal-case tracking-normal">{fmtRpShort(total)}</span>
      </div>
      <div className="mt-2 space-y-2">
        {items.length === 0 && <div className="text-[11px] text-muted-foreground px-1">Tidak ada pos.</div>}
        {items.map((it) => {
          const draggable = !it.virtual && it.remaining > 0.5;
          const changed = it.original !== it.amount || it.billed > 0;
          return (
            <div
              key={it.id}
              draggable={draggable}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", it.id);
                setDragState({ fromId: company.id, itemId: it.id });
              }}
              onDragEnd={() => setDragState(null)}
              className={`group rounded-lg border px-2.5 py-2 flex flex-col justify-between transition-colors ${tone.box} ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${it.virtual ? "border-dashed" : ""}`}
              style={{ minHeight: boxHeight(it.amount, max) }}
              data-testid={`eq-box-${company.id}-${it.id}`}
            >
              <div className="flex items-start gap-1.5">
                {draggable && <GripVertical className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />}
                <span className="text-xs leading-snug text-foreground/90 flex-1 min-w-0 break-words">{it.name}</span>
                {draggable && (
                  <button
                    type="button"
                    onClick={() => onRequestMove({ fromId: company.id, itemId: it.id, toId: null })}
                    className="opacity-70 group-hover:opacity-100 rounded p-1 hover:bg-[hsl(220_10%_14%)] focus-visible:ring-1 focus-visible:ring-primary"
                    aria-label={`Pindahkan ${it.name}`}
                    data-testid={`eq-box-${company.id}-${it.id}-move`}
                  >
                    <MoveRight className="h-3.5 w-3.5 text-primary" />
                  </button>
                )}
              </div>
              <div className="flex items-end justify-between gap-2 mt-1.5">
                <span className={`num text-sm ${tone.text}`}>{fmtRpShort(it.amount)}</span>
                {changed && !it.virtual && it.original !== it.amount && (
                  <span className="num text-[10px] text-muted-foreground line-through">{fmtRpShort(it.original)}</span>
                )}
              </div>
              {(it.virtual || it.billed > 0 || it.moved) && (
                <div className="text-[10px] text-primary/90 mt-1">
                  {it.intercompany ? "Transaksi antar-PT" : it.movedFrom ? `Dipindah dari ${it.movedFrom}` : it.moved ? "Menerima pemindahan" : `Ditagihkan ${fmtRpShort(it.billed)}`}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TransferDialog({ request, companies, preview, onClose, onConfirm }) {
  const open = !!request;
  const from = companies.find((c) => c.id === request?.fromId);
  const item = (preview[request?.fromId] || []).find((i) => i.id === request?.itemId);
  const targets = companies.filter((c) => c.id !== request?.fromId);
  const [toId, setToId] = useState(request?.toId || targets[0]?.id || "");
  const [amount, setAmount] = useState(item ? Math.round(item.remaining / 2) : 0);
  const [mode, setMode] = useState("tagihan");
  const [tx, setTx] = useState("jasa");
  const [note, setNote] = useState("");
  const to = companies.find((c) => c.id === toId);
  const max = item?.remaining || 0;
  const invalid = !toId || amount <= 0 || amount > max + 0.5;

  const explain = !item || !to
    ? ""
    : mode === "realokasi"
    ? `${item.name} ${from?.name} berkurang ${fmtRp(amount)} dan menjadi milik ${to.name}. Kontrak/faktur harus atas nama ${to.name}.`
    : item.section === "beban"
    ? `${from?.name} menagih ${to.name} sebesar ${fmtRp(amount)} (recharge). ${from?.name} mencatat pendapatan, ${to.name} mencatat biaya. PPN dan PPh 23 dihitung sesuai jenis transaksi.`
    : `${to.name} mengerjakan sebagian ${item.name} sebagai subkontraktor ${from?.name} senilai ${fmtRp(amount)}. ${to.name} mencatat pendapatan, ${from?.name} mencatat biaya subkontrak.`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="eq-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="h-5 w-5 text-primary" /> Pindahkan nominal</DialogTitle>
          <DialogDescription>
            {item ? (
              <>
                <span className={item.section === "pendapatan" ? GREEN.text : RED.text}>{item.name}</span> dari {from?.name} · sisa {fmtRp(max)}
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="PT tujuan">
            <Select value={toId} onValueChange={setToId}>
              <SelectTrigger className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-transfer-target"><SelectValue placeholder="Pilih PT" /></SelectTrigger>
              <SelectContent>
                {targets.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Nominal dipindah">
            <MoneyInput value={amount} onChange={setAmount} testid="eq-transfer-amount" />
            <div className="flex gap-1.5 mt-2">
              {[10, 25, 50, 100].map((pct) => (
                <Button key={pct} size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAmount(Math.round((max * pct) / 100))} data-testid={`eq-transfer-pct-${pct}`}>
                  {pct}%
                </Button>
              ))}
            </div>
            {amount > max + 0.5 && <p className="text-[11px] text-[hsl(0_72%_68%)] mt-1" data-testid="eq-transfer-over">Melebihi sisa pos ({fmtRp(max)}).</p>}
          </Field>
          <Field label="Perlakuan">
            <Segmented
              value={mode}
              onChange={setMode}
              testid="eq-transfer-mode"
              options={[
                { value: "tagihan", label: "Tagihan antar-PT" },
                { value: "realokasi", label: "Realokasi biasa" },
              ]}
            />
          </Field>
          {mode === "tagihan" && (
            <Field label="Jenis transaksi">
              <Select value={tx} onValueChange={setTx}>
                <SelectTrigger className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-transfer-txtype"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TX_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Catatan (opsional)">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Mis. kontrak management fee 2025" className="h-10 bg-[hsl(220_12%_8%)]" data-testid="eq-transfer-note" />
          </Field>
          {explain && <InfoNote testid="eq-transfer-explain">{explain}</InfoNote>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="eq-transfer-cancel">Batal</Button>
          <Button
            disabled={invalid}
            className="press"
            onClick={() => onConfirm({ from_id: request.fromId, to_id: toId, item_id: item.id, item_name: item.name, section: item.section, amount, mode, tx_type: tx, note })}
            data-testid="eq-transfer-confirm"
          >
            Pindahkan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeltaBadge({ value, testid }) {
  if (!value) return <Badge variant="outline" className="border-border text-[10px]" data-testid={testid}>Tidak berubah</Badge>;
  const saving = value < 0;
  return (
    <Badge
      variant="outline"
      className={`text-[11px] ${saving ? "border-[hsl(160_45%_35%)] bg-[hsl(160_40%_8%)] text-[hsl(160_55%_62%)]" : "border-[hsl(0_45%_35%)] bg-[hsl(0_40%_9%)] text-[hsl(0_72%_68%)]"}`}
      data-testid={testid}
    >
      {saving ? <TrendingDown className="h-3 w-3 mr-1" /> : <TrendingUp className="h-3 w-3 mr-1" />}
      {saving ? `Hemat ${fmtRpShort(-value)}` : `Tambahan ${fmtRpShort(value)}`}
    </Badge>
  );
}
